<script setup lang="ts">
// swiper
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css/pagination';
import 'swiper/css';

// components
import SvgIcon from '~/components/SvgIcon.vue';
import Cards from '~/components/redesign/Cards';
import Forms from '~/components/redesign/Forms';
import Buttons from '~/components/redesign/Buttons';

// composables
import { useWindowSize } from '@vueuse/core';
import useApiSeo from '~/composables/useApiSeo';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { PATHS } from '~/constants';
import { PostType } from '~/types';

// utils
import get from 'lodash.get';
import { convertArticleToCardsBlogProps, isString } from '~/utils';

const SLIDES_COUNT = 10;

const { width: screenWidth } = useWindowSize();
const localePathWithSlash = useLocalePathWithSlash();

const { data: blogArticleFeaturedData } = await useAsyncGql(
  'BlogArticleFeatured',
);

const featuredArticle = get(
  blogArticleFeaturedData.value,
  'blog_articles.data[0]',
);

const featuredPosts = get(
  featuredArticle,
  'featuredArticleRelationArticles',
  [],
);

const postsToExclude = []
  .concat(get(featuredArticle, 'id'))
  .concat(featuredPosts.map(post => post.id))
  .filter(isString);

const { data: technicalArticlesData } = await useAsyncGql(
  'BlogArticlesPreviewsFiltered',
  {
    first: SLIDES_COUNT,
    type: PostType.POST,
    category_slugs: ['technical'],
    exclude: postsToExclude,
  },
);

const { data: businessArticlesData } = await useAsyncGql(
  'BlogArticlesPreviewsFiltered',
  {
    first: SLIDES_COUNT,
    type: PostType.POST,
    category_slugs: ['business'],
    exclude: postsToExclude,
  },
);

const technicalPosts = get(
  technicalArticlesData.value,
  'blog_articles.data',
  [],
);
const businessPosts = get(businessArticlesData.value, 'blog_articles.data', []);

const SWIPER_CONFIG: Partial<InstanceType<typeof Swiper>> = {
  modules: [Navigation],
  slidesPerView: 'auto',
  breakpoints: {
    1280: {
      spaceBetween: 32,
    },
    768: {
      spaceBetween: 24,
    },
    0: {
      spaceBetween: 8,
    },
  },
};

const sliderOffset = computed(() => {
  const width = screenWidth.value;

  if (width > 1280) {
    return (width - 1230) / 2;
  } else if (width > 767) {
    return 32;
  } else {
    return 16;
  }
});

const subscribeMessage = ref<string>();
const setSubscribeMessage = (message: string) => {
  subscribeMessage.value = message;
};
const isSubscribed = computed(() => isString(subscribeMessage.value));

useApiSeo('blog');
</script>

<template>
  <div data-page-name="blog">
    <section v-if="featuredArticle" class="hero">
      <div class="external-wrapper">
        <h1>Object First {{ $t('Blog') }}</h1>

        <div class="container">
          <Cards.Blog
            v-bind="convertArticleToCardsBlogProps(featuredArticle)"
            orientation="vertical"
          />

          <h2>{{ $t('Featured') }}</h2>
          <ul>
            <li v-for="post of featuredPosts" :key="post.slug">
              <Cards.Blog
                v-bind="convertArticleToCardsBlogProps(post)"
                orientation="horizontal"
              />
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section v-if="technicalPosts.length" class="technical">
      <div class="top">
        <div class="external-wrapper">
          <div class="left">
            <h2>{{ $t('Technical') }}</h2>

            <Buttons.Link
              :to="localePathWithSlash(PATHS.TECHNICAL.path)"
              target="_blank"
              accent="orange"
              fill="outline"
              size="small"
            >
              {{ $t('View_All') }}

              <SvgIcon name="arrow-forward" fill="inherit" />
            </Buttons.Link>
          </div>

          <div class="right">
            <Buttons.Regular
              :aria-label="$t('Awards.prev-btn.title')"
              fill="outline"
              size="small"
              class="technical-prev-slide"
            >
              <SvgIcon name="arrow-backward" fill="inherit" />
            </Buttons.Regular>

            <Buttons.Regular
              :aria-label="$t('Awards.next-btn.title')"
              fill="outline"
              size="small"
              class="technical-next-slide"
            >
              <SvgIcon name="arrow-forward" fill="inherit" />
            </Buttons.Regular>
          </div>
        </div>
      </div>

      <div class="bottom">
        <Swiper
          v-bind="SWIPER_CONFIG"
          :navigation="{
            prevEl: '.technical-prev-slide',
            nextEl: '.technical-next-slide',
          }"
          :slides-offset-after="sliderOffset"
          :slides-offset-before="sliderOffset"
        >
          <SwiperSlide v-for="post of technicalPosts" :key="post.slug">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(post)"
              orientation="vertical"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </section>

    <section v-if="businessPosts.length" class="business">
      <div class="top">
        <div class="external-wrapper">
          <div class="left">
            <h2>{{ $t('Business') }}</h2>

            <Buttons.Link
              :to="localePathWithSlash(PATHS.BUSINESS.path)"
              target="_blank"
              accent="orange"
              fill="outline"
              size="small"
            >
              {{ $t('View_All') }}

              <SvgIcon name="arrow-forward" fill="inherit" />
            </Buttons.Link>
          </div>

          <div class="right">
            <Buttons.Regular
              :aria-label="$t('Awards.prev-btn.title')"
              fill="outline"
              size="small"
              class="business-prev-slide"
            >
              <SvgIcon name="arrow-backward" fill="inherit" />
            </Buttons.Regular>

            <Buttons.Regular
              :aria-label="$t('Awards.next-btn.title')"
              fill="outline"
              size="small"
              class="business-next-slide"
            >
              <SvgIcon name="arrow-forward" fill="inherit" />
            </Buttons.Regular>
          </div>
        </div>
      </div>

      <div class="bottom">
        <Swiper
          v-bind="SWIPER_CONFIG"
          :navigation="{
            prevEl: '.business-prev-slide',
            nextEl: '.business-next-slide',
          }"
          :slides-offset-after="sliderOffset"
          :slides-offset-before="sliderOffset"
        >
          <SwiperSlide v-for="post of businessPosts" :key="post.slug">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(post)"
              orientation="vertical"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </section>

    <section class="product-news">
      <div class="external-wrapper">
        <div class="inner-wrapper">
          <div :class="['subscribe-form', { hidden: isSubscribed }]">
            <h3>{{ $t('Product_news') }}</h3>
            <Forms.Subscribe @change="setSubscribeMessage" />
          </div>

          <div :class="['subscribe-message', { hidden: !isSubscribed }]">
            <span v-html="subscribeMessage" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/position';
@use '_/mixins/text-transform';
@use '_/fn';

.external-wrapper {
  padding: 0 2rem;
  @include size.wrapper(80rem);

  @include media.mobile {
    padding: 0 1rem;
  }
}

.inner-wrapper {
  @include size.wrapper(37rem);

  @include media.tablet {
    @include size.wrapper(100%);
  }
}

.hidden {
  opacity: 0;
  pointer-events: none;
}

[data-page-name='blog'] {
  padding-top: 5rem;

  @include media.tablet {
    padding-top: 2.2rem;
  }

  @include media.mobile {
    padding-top: 1.5rem;
  }

  section.hero {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 10rem;
    }

    @include media.mobile {
      margin-bottom: 5.7rem;
    }

    .external-wrapper {
      h1 {
        @include typo.h2;
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 2rem;
        }

        @include media.mobile {
          margin-bottom: 1.5rem;
        }
      }

      .container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto 1fr;
        grid-gap: 2rem 2rem;
        grid-template-areas:
          'featured title'
          'featured list';

        @include media.tablet {
          grid-template-columns: 44.4vw 1fr;
          grid-gap: 1.5rem;
          grid-template-areas:
            'title title'
            'featured list';
        }

        @include media.mobile {
          grid-template-columns: 1fr;
          grid-template-areas:
            'title'
            'featured'
            'list';
        }

        > :deep([data-component-name='Cards.Blog']) {
          grid-area: featured;

          @include media.tablet {
            margin-top: 1rem;
          }

          @include media.mobile {
            margin-top: 0;
          }

          .title {
            @include typo.h4;
          }
        }

        h2 {
          grid-area: title;
          @include typo.h3;
          margin-top: 0.4rem;
          height: min-content;

          @include media.tablet {
            margin-top: 0;
          }
        }

        ul {
          grid-area: list;
          margin-top: -1.75rem;

          @include media.tablet {
            margin-top: 0;
          }

          li {
            &:not(:last-of-type) {
              border-bottom: 1px solid fn.token('outline-secondary');
            }
          }
        }
      }
    }
  }

  section.technical,
  section.business {
    margin-bottom: 10rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .top {
      margin-bottom: 2rem;

      @include media.tablet {
        margin-bottom: 1.5rem;
      }

      .external-wrapper {
        @include flex.center-sb;

        .left {
          @include flex.center-sb;
          gap: 0 2rem;

          @include media.mobile {
            width: 100%;
          }

          h2 {
            @include typo.h3;
          }
        }

        .right {
          @include flex.stretch-sb;
          gap: 0 2rem;

          @include media.mobile {
            display: none;
          }

          [data-component-name='Buttons.Regular'] {
            svg {
              transition: fill 0.15s ease;
            }

            &.swiper-button-disabled {
              svg {
                fill: fn.token('icon-tertiary');
              }
            }
          }
        }
      }
    }

    .bottom {
      :deep(.swiper) {
        padding-bottom: 1.5rem;

        .swiper-wrapper {
          .swiper-slide {
            @include size.fixed-width(24rem);

            @include media.tablet {
              @include size.fixed-width(21.25rem);
            }

            @include media.mobile {
              @include size.fixed-width(20.5rem);
            }

            [data-component-name='Cards.Blog'] {
              min-height: 28.3rem;

              @include media.tablet {
                min-height: 26rem;
              }

              @include media.mobile {
                min-height: 24.625rem;
              }
            }
          }
        }
      }
    }
  }

  section.product-news {
    padding: 5rem 0;
    background-color: fn.token('surf-cont-secondary');

    @include media.tablet {
      padding: 4rem 0;
    }

    @include media.mobile {
      padding: 1.5rem 0;
    }

    .external-wrapper,
    .subscribe-message {
      transition: opacity 0.15s ease;
    }

    .external-wrapper {
      .inner-wrapper {
        position: relative;

        .subscribe-form {
          h3 {
            @include typo.subtitle-1;
            margin-bottom: 1rem;
          }
        }

        .subscribe-message {
          @include position.absolute-full;
          @include flex.center-center;
          @include typo.subtitle-1;
          text-align: center;
        }
      }
    }
  }
}
</style>
